import { inject, Injectable, OnDestroy } from '@angular/core';
import { bufferTime, catchError, EMPTY, filter, from, map, of, Subject, switchMap, tap } from 'rxjs';
import { ApiService } from '../api/api.service';
import { AccountService } from '../account';

export enum WeblogCategory {ui = 'ui'}

@Injectable({
  providedIn: 'root'
})
export class WebLoggerService implements OnDestroy {

  private api = inject(ApiService)
  private account = inject(AccountService)
  private errorSubject = new Subject<{category: WeblogCategory, error: Record<string, unknown>}>()
  private isBlocked = false
  private hashes: string[] = []

  constructor() {
    this.errorSubject.pipe(
      bufferTime(10_000),
      filter(errors => errors.length > 0),
      switchMap(errors => this.isBlocked ? EMPTY : this.api.post('/weblog/log-errors', {errors})),
      catchError(() => {
        this.isBlocked = true
        setTimeout(() => this.isBlocked = false, 60_000)
        return EMPTY
      })
    ).subscribe()
  }

  ngOnDestroy(): void {
    this.errorSubject.complete()
  }

  /**
   * Форматирует и прокидывает ошибку дальше, если такой еще не зарегистрировано
   */
  reportError(category: WeblogCategory, error: Record<string, unknown>) {
    this.formatError(error).pipe(
      switchMap(obj => {
        if (Object.keys(obj).length <= 1) {
          return EMPTY
        }
        return from(this.computeHash(obj)).pipe(
          map(hash => ({obj, hash}))
        )
      }),
      tap(({obj, hash}) => {
        if (!this.hashes.includes(hash)) {
          this.errorSubject.next({
            category,
            error: obj
          })
          this.hashes.push(hash)
          if (this.hashes.length > 1000) {
            this.hashes.shift()
          }
        }
      })
    ).subscribe()
  }

  private formatError(error: Record<string, unknown>) {
    return this.account.getProfile().pipe(
      map(profile => profile && typeof error == 'object' && error ? {accId: profile.id, ...error} : error),
      catchError(() => of(error))
    )
  }

  private async computeHash(obj: Record<string, unknown>) {
    const payload = JSON.stringify(obj)
    const subtle = globalThis.crypto?.subtle
    if (!subtle) {
      return payload
    }
    try {
      const data = new TextEncoder().encode(payload)
      const buffer = await subtle.digest('SHA-256', data)
      return Array.from(new Uint8Array(buffer))
        .map(byte => byte.toString(16).padStart(2, '0'))
        .join('')
    } catch {
      return payload
    }
  }

}
